import { FC, useCallback, useMemo, useEffect } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { useDialog } from '@/_shared/hooks/useDialog';
import { Sort } from '@/swagger/constants/contents-orchestrator-enums';

type OrderProps = {
  sortBy?: `${Sort}`;
};

type Props = {
  appliedOrder?: OrderProps;
  onChangeOrder?: ({ sortBy }: OrderProps) => void;
};

export const OrderBy: FC<Props> = ({ appliedOrder, onChangeOrder }) => {
  // eslint-disable-next-line @altermaind/use-translations-scope
  const t = useTranslations('trainingSoftSkills.filtersAndOrderContent.orders');
  const { closeDialog: closeDialogOrderBy } = useDialog('orderBy');

  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      sortBy: appliedOrder?.sortBy || Sort.MOSTRECENT,
    },
    mode: 'onChange',
  });

  const sortByValue = watch('sortBy');

  const radioItems = useMemo(() => {
    return [
      {
        label: t(`sortBy.${Sort.MOSTRECENT}`),
        value: Sort.MOSTRECENT,
        index: 0,
        selected: !appliedOrder?.sortBy || appliedOrder?.sortBy === Sort.MOSTRECENT,
      },
      {
        label: t(`sortBy.${Sort.HIGHESTRATING}`),
        value: Sort.HIGHESTRATING,
        index: 1,
        selected: appliedOrder?.sortBy === Sort.HIGHESTRATING,
      },
    ];
  }, [appliedOrder?.sortBy, t]);

  const onChangeOrderSubmit: SubmitHandler<FieldValues> = useCallback(
    data => {
      if (onChangeOrder) {
        onChangeOrder({
          sortBy: data.sortBy,
        });
      }
      closeDialogOrderBy();
    },
    [onChangeOrder, closeDialogOrderBy],
  );

  // Trigger submit when sortBy value changes
  useEffect(() => {
    // first run
    if (!isSubmitted && sortByValue === Sort.MOSTRECENT) {
      return;
    }
    handleSubmit(onChangeOrderSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortByValue]);

  return (
    <Box>
      <Box display='flex' flexDirection='column'>
        <form onSubmit={handleSubmit(onChangeOrderSubmit)}>
          <Box>
            <Form.FormRadio
              zIndex={9999}
              // @ts-ignore
              control={control}
              name='sortBy'
              items={radioItems}
              radioItemAlign='vertical'
            />
          </Box>
        </form>
      </Box>
    </Box>
  );
};
